// src/context/NotificationContext.js
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';

const STORAGE_KEY = 'vitra_unread_messages_count';
const STORAGE_KEY_MAP = 'vitra_unread_by_conversation';

function readStoredCount() {
  try {
    const s = sessionStorage.getItem(STORAGE_KEY);
    if (s == null) return 0;
    const n = parseInt(s, 10);
    return Number.isNaN(n) || n < 0 ? 0 : Math.min(n, 999);
  } catch {
    return 0;
  }
}

function writeStoredCount(count) {
  try {
    const n = Math.max(0, Math.min(999, Number(count) | 0));
    sessionStorage.setItem(STORAGE_KEY, String(n));
  } catch {
    /* ignore */
  }
}

/** Read per-conversation unread from sessionStorage so highlighting persists until user reads (e.g. across refresh). */
function readStoredUnreadMap() {
  try {
    const s = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(STORAGE_KEY_MAP) : null;
    if (s == null) return { map: new Map(), total: 0 };
    const arr = JSON.parse(s);
    if (!Array.isArray(arr)) return { map: new Map(), total: 0 };
    const map = new Map();
    let total = 0;
    arr.forEach((e) => {
      const cid = e?.conversationId != null ? String(e.conversationId) : null;
      if (!cid) return;
      const count = Math.max(0, Math.min(999, Number(e.count) | 0));
      if (count <= 0) return;
      map.set(cid, { count, displayName: (e?.displayName ?? '').toString() });
      total += count;
    });
    return { map, total: Math.min(999, total) };
  } catch {
    return { map: new Map(), total: 0 };
  }
}

let storedUnreadSnapshot = null;
function getStoredUnreadSnapshot() {
  if (!storedUnreadSnapshot) storedUnreadSnapshot = readStoredUnreadMap();
  return storedUnreadSnapshot;
}

function writeStoredUnreadMap(map) {
  try {
    if (typeof sessionStorage === 'undefined' || !map || !(map instanceof Map)) return;
    const arr = [];
    map.forEach((entry, conversationId) => {
      const count = entry?.count ?? 0;
      if (count <= 0) return;
      arr.push({ conversationId, count, displayName: entry?.displayName ?? '' });
    });
    sessionStorage.setItem(STORAGE_KEY_MAP, JSON.stringify(arr));
  } catch {
    /* ignore */
  }
}

const NotificationContext = createContext(null);

/** @typedef {{ count: number, displayName?: string }} UnreadEntry */
/** @typedef {Map<string, UnreadEntry>} UnreadByConversation */

export const NotificationProvider = ({ children }) => {
  const [unreadMessagesCount, setUnreadMessagesCountState] = useState(() => {
    const s = getStoredUnreadSnapshot();
    return s.map.size > 0 ? s.total : readStoredCount();
  });
  const [unreadByConversation, setUnreadByConversation] = useState(() => {
    const s = getStoredUnreadSnapshot();
    return s.map.size > 0 ? new Map(s.map) : new Map();
  });

  // Persist per-conversation unread so highlighting survives refresh until user reads
  useEffect(() => {
    writeStoredUnreadMap(unreadByConversation);
  }, [unreadByConversation]);

  const setUnreadMessagesCount = useCallback((value) => {
    const count = Math.max(0, Math.min(999, Number(value) | 0));
    setUnreadMessagesCountState(count);
    writeStoredCount(count);
  }, []);

  /** Clear unread for one conversation (call when user opens that conversation). */
  const clearUnreadForConversation = useCallback((conversationId) => {
    const key = conversationId != null ? String(conversationId) : '';
    if (!key) return;
    setUnreadByConversation((prev) => {
      const entry = prev.get(key);
      if (!entry) return prev;
      const subtract = entry.count ?? 0;
      const next = new Map(prev);
      next.delete(key);
      setUnreadMessagesCountState((p) => {
        const n = Math.max(0, p - subtract);
        writeStoredCount(n);
        return n;
      });
      return next;
    });
  }, []);

  const hydrateUnread = useCallback((entries, options = {}) => {
    const replace = Boolean(options.replace);
    setUnreadByConversation((prev) => {
      const next = replace ? new Map() : new Map(prev);
      if (!Array.isArray(entries) || entries.length === 0) {
        if (replace) {
          setUnreadMessagesCountState(0);
          writeStoredCount(0);
        }
        return next;
      }
      entries.forEach((e) => {
        const cid = e?.conversationId != null ? String(e.conversationId) : null;
        if (!cid) return;
        const serverCount = Math.max(0, Math.min(999, Number(e.count) | 0));
        const existing = replace ? 0 : (next.get(cid)?.count ?? 0);
        const count = Math.max(serverCount, existing);
        if (count <= 0) {
          next.delete(cid);
          return;
        }
        next.set(cid, { count, displayName: e?.displayName ?? '' });
      });
      let total = 0;
      next.forEach((entry) => { total += entry?.count ?? 0; });
      setUnreadMessagesCountState(Math.min(999, total));
      writeStoredCount(Math.min(999, total));
      return next;
    });
  }, []);

  /** Increment unread (e.g. on new incoming message). Pass conversationId and optional displayName for per-conversation badge. */
  const incrementUnread = useCallback((delta = 1, opts = {}) => {
    const conversationId = opts?.conversationId != null ? String(opts.conversationId) : null;
    const displayName = (opts?.displayName ?? '').toString();

    if (conversationId) {
      setUnreadByConversation((prev) => {
        const next = new Map(prev);
        const entry = next.get(conversationId) || { count: 0, displayName: '' };
        const count = Math.max(0, Math.min(999, (entry.count || 0) + (Number(delta) | 0)));
        next.set(conversationId, { count, displayName: displayName || entry.displayName });
        return next;
      });
    }

    setUnreadMessagesCountState((prev) => {
      const next = Math.max(0, Math.min(999, prev + (Number(delta) | 0)));
      writeStoredCount(next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      unreadMessagesCount,
      setUnreadMessagesCount,
      incrementUnread,
      unreadByConversation,
      clearUnreadForConversation,
      hydrateUnread,
    }),
    [unreadMessagesCount, setUnreadMessagesCount, incrementUnread, unreadByConversation, clearUnreadForConversation, hydrateUnread]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const ctx = useContext(NotificationContext);
  return (
    ctx || {
      unreadMessagesCount: 0,
      setUnreadMessagesCount: () => {},
      incrementUnread: () => {},
      unreadByConversation: new Map(),
      clearUnreadForConversation: () => {},
      hydrateUnread: () => {},
    }
  );
};
